// Preset selector shown at the top of the OGS / Leveling intake tabs.
//
// Renders one pill per model-defined preset plus a "Custom" pill. Selecting a
// preset applies its saved slider values (via onApply); the sliders below stay
// fully editable afterwards, at which point the parent flips the active id to
// 'custom'. Rendered only when the model actually defines presets.

import clsx from 'clsx';

interface PresetOption {
  id: string;
  name: string;
}

export default function PresetBar({
  presets,
  activeId,
  onApply,
  onCustom,
  note,
}: {
  presets: PresetOption[];
  activeId?: string;
  onApply: (id: string) => void;
  onCustom: () => void;
  note?: string;
}) {
  if (!presets || presets.length === 0) return null;
  const isCustom = !activeId || activeId === 'custom' || !presets.some((p) => p.id === activeId);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 mb-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 shrink-0">
          Presets
        </span>
        {presets.map((p) => {
          const active = activeId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onApply(p.id)}
              className={clsx(
                'pill border transition-colors',
                active
                  ? 'bg-brand-purple-100 text-brand-purple-700 border-brand-purple-300'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              {p.name}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onCustom}
          className={clsx(
            'pill border transition-colors',
            isCustom
              ? 'bg-slate-200 text-slate-700 border-slate-300'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
          )}
        >
          Custom
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mt-1.5">
        {note || 'Applying a preset overwrites the sliders below — you can still fine-tune after.'}
      </p>
    </div>
  );
}
